import { Utils } from '@/types/utils';
import { nanoid } from 'nanoid';
import {
    ComponentProps,
    ReactNode,
    Suspense,
    lazy,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router';

import {
    DialogActionsContext,
    DialogStateContext,
    IActiveDialog,
    IDialogOptions,
} from './DialogContext';
import { ManagedAxleDialogProps } from './ManagedAxleDialog';

/**********************************************************
 * Dialog Manager - a high-level component and context that
 * allows us to invoke dialogs from anywhere in the app without
 * having to manage dialog state manually.
 *
 * The DialogManager expects to be used only for those
 * dialog components that use <ManagedAxleDialog />, as
 * that allows us to get the nice slide-in/-out animation
 * without the parent component having to manage the isOpen
 * state to do so.
 *
 * Onboarding a dialog into this manager involves:
 * 1. Creating your new dialog with <ManagedAxleDialog />
 * 2. Adding a new entry to the DialogType enum below.
 * 3. Adding a new entry to the DialogLazyComponents record below.
 *
 * Then, in the component that wants to open the dialog, you can
 * use the `openDialog` function provided by the `useDialog`
 * hook to open the dialog.
 *
 * ----------------------------------------------------------
 * Example:
 * [in DialogManager.tsx]
 * export enum DialogType {
 *   ...
 *   MyNewDialog,
 *   ...
 * }
 *
 * const DialogLazyComponents = {
 *   ...
 *   [DialogType.MyNewDialog]: lazy(() => import('./path/to/MyNewDialog').then((module) => ({
 *     default: module.MyNewDialog,
 *   })),
 *   ...
 * }
 *
 * [in the component that wants to open the dialog]
 * const { openDialog } = useDialog();
 * openDialog(DialogType.MyNewDialog, {
 *   // Any props you want to pass to the dialog
 *   // ...
 * });
 * ----------------------------------------------------------
 *
 * The dialog components are loaded lazily, in case we want to
 * do code-splitting in the future.
 *
 * Implementation loosely adapted from:
 * https://hackernoon.com/mastering-modal-dialogs-in-react-like-a-pro
 **********************************************************/

export enum DialogType {
    // Common Utility Dialogs
    Confirm,

    //Clinician Details
    DeactivateProfile,

    // Clinician Teams
    ClinicianTeamDetails,
    EditClinicianTeam,
    CreateClinicianTeam,

    // Episodes
    EditEpisodeOfCareNotes,

    // Orders
    FilterOrders,

    // Patients
    CreatePatient,
    EditPatientCareTeam,
    EditPatientCareTeamRecommendation,
    EditPatientCommsPreferences,
    EditPatientNotes,
    EditPatientTimeslotPreferenceList,
    EditPatientTimeslotPreferences,
    CreatePatientTimeslotPreferenceItem,
    EditPatientTimeslotPreferenceItem,
    CreatePatientVisit,

    // Patients (Legacy)
    LegacyEditPatient,

    // Shifts
    CreateShift,
    EditShift,

    // Shift Blocks
    CreateShiftBlock,
    EditShiftBlock,

    // Shift Block Requests
    ReviewShiftBlockRequest,

    // Territories
    CreateTerritory,

    // VisitRequests
    EditVisitRequest,
    FilterVisitRequests,

    // Visits
    BulkCancelVisits,
    EditVisit,
    EditVisitServices,
    FilterVisits,
    MarkVisitAsMissed,
    ReassignVisits,
    SchedulingConflict,

    // Visits (Legacy)
    LegacyExportVisits,

    DateDecisionSupport,
    LegacyClinicianDecisionSupport,
    ClinicianDecisionSupport,
    AssignmentClinicianDecisionSupport,
    AssignmentDateDecisionSupport,

    // Urgent Booking
    UrgentBookingFlow,
}

export { useActiveDialogs, useDialog } from './DialogContext';

// Root-level component that provides the context and handles rendering active dialogs.
export const DialogProvider = ({ children }: { children: ReactNode }) => {
    const { openDialog, activeDialogs } = useDialogProvider();

    // Memoized separately from activeDialogs so its identity is stable across
    // dialog open/close. Consumers of `useDialog()` therefore never re-render
    // when a dialog is opened or closed.
    const actions = useMemo(() => ({ openDialog }), [openDialog]);

    return (
        <DialogActionsContext.Provider value={actions}>
            <DialogStateContext.Provider value={activeDialogs}>
                {children}
                {typeof window !== 'undefined' && activeDialogs.length ? (
                    <>
                        {activeDialogs.map(({ id, component: LazyDialog }) => (
                            <Suspense fallback={null} key={id}>
                                <LazyDialog />
                            </Suspense>
                        ))}
                    </>
                ) : null}
            </DialogStateContext.Provider>
        </DialogActionsContext.Provider>
    );
};

// Tells us where to find the dialog components for each dialog type.
const DialogLazyComponents = {
    [DialogType.DeactivateProfile]: lazy(() =>
        import('../components/profiles/dialogs/DeactivateProfileDialog').then((module) => ({
            default: module.DeactivateProfileDialog,
        })),
    ),

    // Clinician Teams
    [DialogType.ClinicianTeamDetails]: lazy(() =>
        import('../pages/QhpDetailsPage/ClinicianTeamDetailsDialog').then((module) => ({
            default: module.ClinicianTeamDetailsDialog,
        })),
    ),
    [DialogType.CreateClinicianTeam]: lazy(() =>
        import('../pages/SupplyPage/Qhps/CreateClinicianTeamDialog').then((module) => ({
            default: module.CreateClinicianTeamDialog,
        })),
    ),
    [DialogType.EditClinicianTeam]: lazy(() =>
        import('../pages/SupplyPage/Qhps/EditClinicianTeamDialog').then((module) => ({
            default: module.EditClinicianTeamDialog,
        })),
    ),
    [DialogType.DateDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/DateDecisionSupport').then((module) => ({
            default: module.DateDecisionSupportDialog,
        })),
    ),
    [DialogType.LegacyClinicianDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/LegacyClinicianDecisionSupport').then((module) => ({
            default: module.LegacyClinicianDecisionSupportDialog,
        })),
    ),
    [DialogType.ClinicianDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/ClinicianDecisionSupport').then((module) => ({
            default: module.ClinicianDecisionSupportDialog,
        })),
    ),
    [DialogType.AssignmentClinicianDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/AssignmentClinicianDecisionSupport').then(
            (module) => ({
                default: module.AssignmentClinicianDecisionSupportDialog,
            }),
        ),
    ),
    [DialogType.AssignmentDateDecisionSupport]: lazy(() =>
        import('../components/decisionSupport/AssignmentDateDecisionSupport').then((module) => ({
            default: module.AssignmentDateDecisionSupportDialog,
        })),
    ),

    // Common Utility Dialogs
    [DialogType.Confirm]: lazy(() =>
        import('../components/layout/AxleConfirmDialog').then((module) => ({
            default: module.ManagedAxleConfirmDialog,
        })),
    ),

    // Episodes
    [DialogType.EditEpisodeOfCareNotes]: lazy(() =>
        import('../components/episodes/EditEpisodeNotesDialog').then((module) => ({
            default: module.EditEpisodeNotesDialog,
        })),
    ),

    // Orders
    [DialogType.FilterOrders]: lazy(() =>
        import('../components/orders/OrderFilterDialog').then((module) => ({
            default: module.OrderFilterDialog,
        })),
    ),

    // Patients
    [DialogType.CreatePatient]: lazy(() =>
        import('../components/patients/dialogs/CreatePatientDialog').then((module) => ({
            default: module.CreatePatientDialog,
        })),
    ),
    [DialogType.EditPatientCareTeam]: lazy(() =>
        import('../components/patients/dialogs/EditPatientCareTeam/EditPatientCareTeamDialog').then(
            (module) => ({
                default: module.EditPatientCareTeamDialog,
            }),
        ),
    ),
    [DialogType.EditPatientCareTeamRecommendation]: lazy(() =>
        import(
            '../components/patients/dialogs/EditPatientCareTeam/EditPatientCareTeamRecommendationDialog'
        ).then((module) => ({
            default: module.EditPatientCareTeamRecommendationDialog,
        })),
    ),
    [DialogType.EditPatientCommsPreferences]: lazy(() =>
        import('../components/patients/dialogs/EditPatientCommsPreferencesDialog').then(
            (module) => ({
                default: module.EditPatientCommsPreferencesDialog,
            }),
        ),
    ),
    [DialogType.EditPatientNotes]: lazy(() =>
        import('../components/patients/dialogs/EditPatientNotesDialog').then((module) => ({
            default: module.EditPatientNotesDialog,
        })),
    ),
    [DialogType.EditPatientTimeslotPreferenceList]: lazy(() =>
        import('../components/patients/dialogs/EditPatientTimeslotPreferenceListDialog').then(
            (module) => ({
                default: module.EditPatientTimeslotPreferenceListDialog,
            }),
        ),
    ),
    [DialogType.EditPatientTimeslotPreferences]: lazy(() =>
        import(
            '../components/patients/dialogs/StandaloneEditPatientTimeslotPreferenceListDialog'
        ).then((module) => ({
            default: module.StandaloneEditPatientTimeslotPreferenceListDialog,
        })),
    ),
    [DialogType.CreatePatientTimeslotPreferenceItem]: lazy(() =>
        import('../components/patients/dialogs/CreatePatientTimeslotPreferenceItemDialog').then(
            (module) => ({
                default: module.CreatePatientTimeslotPreferenceItemDialog,
            }),
        ),
    ),
    [DialogType.EditPatientTimeslotPreferenceItem]: lazy(() =>
        import('../components/patients/dialogs/EditPatientTimeslotPreferenceItemDialog').then(
            (module) => ({
                default: module.EditPatientTimeslotPreferenceItemDialog,
            }),
        ),
    ),
    [DialogType.CreatePatientVisit]: lazy(() =>
        import('../components/patients/dialogs/CreatePatientVisit/CreatePatientVisit').then(
            (m) => ({
                default: m.CreatePatientVisitDialog,
            }),
        ),
    ),

    // Patients (Legacy)
    [DialogType.LegacyEditPatient]: lazy(() =>
        import('../pages/legacy/LegacyPatientDetailsPage/LegacyEditPatientDialog').then(
            (module) => ({
                default: module.LegacyEditPatientDialog,
            }),
        ),
    ),

    // Shifts
    [DialogType.CreateShift]: lazy(() =>
        import('../components/shifts/dialogs/CreateShiftDialog').then((module) => ({
            default: module.CreateShiftDialog,
        })),
    ),
    [DialogType.EditShift]: lazy(() =>
        import('../components/shifts/dialogs/EditShiftDialogs').then((module) => ({
            default: module.EditShiftDialog,
        })),
    ),

    // Shift Blocks
    [DialogType.CreateShiftBlock]: lazy(() =>
        import('../components/shifts/dialogs/CreateShiftBlockDialog').then((module) => ({
            default: module.CreateShiftBlockDialog,
        })),
    ),
    [DialogType.EditShiftBlock]: lazy(() =>
        import('../components/shifts/dialogs/EditShiftBlockDialog').then((module) => ({
            default: module.EditShiftBlockDialog,
        })),
    ),

    // Shift Block Requests
    [DialogType.ReviewShiftBlockRequest]: lazy(() =>
        import(
            '../pages/SchedulePage/ClinicianScheduleRequestsTab/ReviewShiftBlockRequestDialog'
        ).then((module) => ({
            default: module.ReviewShiftBlockRequestDialog,
        })),
    ),

    // Territories
    [DialogType.CreateTerritory]: lazy(() =>
        import('../pages/TerritoriesPage/CreateTerritoryDialog').then((module) => ({
            default: module.CreateTerritoryDialog,
        })),
    ),

    // Visit Requests
    [DialogType.EditVisitRequest]: lazy(() =>
        import('../components/visitRequests/EditVisitRequestDialog').then((module) => ({
            default: module.EditVisitRequestDialog,
        })),
    ),
    [DialogType.FilterVisitRequests]: lazy(() =>
        import('../components/visitRequests/VisitRequestFilterDialog').then((module) => ({
            default: module.VisitRequestFilterDialog,
        })),
    ),

    // Visits
    [DialogType.BulkCancelVisits]: lazy(() =>
        import('../components/visits/dialogs/BulkCancelVisits/BulkCancelVisitsDialog').then(
            (module) => ({
                default: module.BulkCancelVisitsDialog,
            }),
        ),
    ),
    [DialogType.EditVisit]: lazy(() =>
        import('../components/visits/EditVisitDialog').then((module) => ({
            default: module.EditVisitDialog,
        })),
    ),
    [DialogType.EditVisitServices]: lazy(() =>
        import('../components/visits/dialogs/EditVisitServices/EditVisitServicesDialog').then(
            (module) => ({
                default: module.EditVisitServicesDialog,
            }),
        ),
    ),
    [DialogType.FilterVisits]: lazy(() =>
        import('../components/visits/VisitFilterDialog').then((module) => ({
            default: module.VisitFilterDialog,
        })),
    ),
    [DialogType.MarkVisitAsMissed]: lazy(() =>
        import('../components/visits/MarkMissedDialog').then((module) => ({
            default: module.MarkVisitMissedDialog,
        })),
    ),
    [DialogType.ReassignVisits]: lazy(() =>
        import('../components/visits/dialogs/ReassignVisits/ReassignVisitsDialog').then(
            (module) => ({
                default: module.ReassignVisitsDialog,
            }),
        ),
    ),
    [DialogType.SchedulingConflict]: lazy(() =>
        import('../pages/VisitBooking/scheduling/SchedulingConflictDialog').then((module) => ({
            default: module.SchedulingConflictDialog,
        })),
    ),

    // Urgent Booking
    [DialogType.UrgentBookingFlow]: lazy(() =>
        import('../components/patients/dialogs/UrgentBookingFlow/UrgentBookingFlow').then(
            (module) => ({
                default: module.UrgentBookingFlowDialog,
            }),
        ),
    ),

    // Visits (Legacy)
    [DialogType.LegacyExportVisits]: lazy(() =>
        import('../pages/legacy/LegacyVisitsPage/LegacyExportVisitsDialog').then((module) => ({
            default: module.LegacyExportVisitsDialog,
        })),
    ),
};

// Exported so DialogContext.ts can reference the lazy component map in its type definitions.
export type DialogLazyComponentsType = typeof DialogLazyComponents;

const DIALOG_DEFAULT_OPTION_CONFIG: IDialogOptions = {
    closeOnNavigationChange: true,
};

const DialogDefaultOptions: Partial<Record<DialogType, IDialogOptions>> = {};

const getDialogDefaultOptions = (type: DialogType) => {
    return {
        ...DIALOG_DEFAULT_OPTION_CONFIG,
        ...(DialogDefaultOptions[type] ?? {}),
    };
};

// Internally-used hook to initialize the root provider.
const useDialogProvider = () => {
    const [activeDialogs, setActiveDialogs] = useState<IActiveDialog[]>([]);
    const location = useLocation();
    const isMounted = useRef(false);

    const unmountDialog = useCallback(
        (id: string) => {
            setActiveDialogs((prev) => prev.filter((d) => d.id !== id));
        },
        [setActiveDialogs],
    );

    // Listen for navigation changes and close dialogs that are configured to do so.
    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;
            return;
        }
        activeDialogs.forEach(({ id, options }) => {
            if (options.closeOnNavigationChange) {
                unmountDialog(id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const open = useCallback(
        <
            DialogT extends DialogType,
            DialogComponentPropsT extends ComponentProps<(typeof DialogLazyComponents)[DialogT]>,
        >(
            type: DialogT,
            props: Omit<DialogComponentPropsT, keyof ManagedAxleDialogProps>,
            options?: IDialogOptions,
        ) => {
            const id = nanoid();
            const Component = DialogLazyComponents[type];
            const dialogOptions = {
                ...getDialogDefaultOptions(type),
                ...options,
            };

            setActiveDialogs((prev) => [
                ...prev,
                {
                    id,
                    options: dialogOptions,
                    type,
                    component: () => (
                        // TODO(jessica): See if we can tighten this up, though, it doesn't seem necessarily important to do so,
                        // in that openDialog already gives proper hints/errors inferred from the underlying dialog type.
                        <Component
                            {...(props as Utils.UNSAFE_ANY)}
                            onClose={() => unmountDialog(id)}
                        />
                    ),
                },
            ]);
        },
        [unmountDialog],
    );

    // `open` is a stable callback (memoized on `unmountDialog`), while
    // `activeDialogs` is state. DialogProvider wraps each in its own context so
    // that opening a dialog doesn't re-render components that only need `open`.
    return { openDialog: open, activeDialogs };
};
